/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { debounce } from 'lodash';
import Vue from 'vue';
import { ActionContext, Module } from 'vuex';
import store from '@/store';
import router from '@/router';
import { RouterV2Enum } from '@/router/router-names';
import { Toast, Dialog } from '@hb/coffee';

import type RootStateType from '@/store/types';
import generateMutations, {
  UPDATE_STATE_TYPE,
} from '@/store/generate-mutations';

import {
  getChannelList,
  getApplyProduct,
  calcRepayPlans,
  getLoanUsageList,
  getLoanContracts,
  checkNeedTradePasswd,
  submitOrderApply,
  sendApplySms,
  confirmApplySms,
  checkAlipayStatus,
  getConfigCityItems,
  isBankCardUsed,
  orderQueryRealTime,
  orderApply,
  orderUpdateFailed,
  orderCheck,
  applyAfterSmsAuth,
  alipayOrBank,
  getAllinpayContractUrl,
} from '@/api/apply-product';

import {
  ApplyChannelData,
  SmsConfirmResult,
  ChannelApplyLimitEnum,
  CardBindTypeEnum,
  ChannelDisableEnum,
  ProductCapitalItem,
  LoanApplyMarketReqType,
  pageSceneEnum,
  AccountType,
  CalcPlanListData,
  RateTypeEnum,
  UserSceneEnum,
} from '@/api/apply-product/types';

import { PeriodTypeValue } from '@/enums/period-type';
import type { ContractItem } from '@/ContractConfig';
import { RepaymentModel, RepayMentModelType, repayPlanModelType } from '@/po/ProductResult';
import { ResponseCodeEnum } from '@/enums/response-code';
import ProductCommodity, {
  DUXIAOMAN_COMMODITY,
  YINHE_COMMODITY,
  isNewYhChannel,
} from '@/shared/commodity';
import { ABResEnum } from '@/enums/abExperiment';
import { LoanAgreementPO } from '@/api/contract/types';
import { SmsSceneType } from '@/enums/scene-type';
import { getRuleSetCouponList } from '@/api/coupon';
import { ProductContracts } from '@/ContractConfig';
import { getAgreementsByCommodities } from '@/api/contract';
import { ContractEnum } from '@/enums/contract-type';
import type { Commodity } from '@/api/contract/types';
import { ubtSendError } from '@/shared/utils/ubt';
import { ubtSetupPageButton, ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { getCheckResult, getGalileoExpResult, checkDualChannelLight36 } from '@/api/rights';
import { getGalileoAb } from '@/shared/galileoAb';
import {
  AutrakyProcessExpEnum, GalileoExpSceneEnum, RightPackageSourceEnum, RightPackageTypeEnum,
} from '@/api/rights/types';
import './loanRightStore';
import URLUtils from '@/shared/utils/URLUtils';
import { showLoanPageTempAmountEntry, applyTempAmount } from '@/api/tempAmount';
import { TempAmountSceneEnum, EventLoopNotifySceneEnum } from '@/api/tempAmount/types';
import { EquitySelectedEnum, isReCalcRequiredForPricingType } from '@/enums/rights';
import { CALC_REPAYPLANS_VERSION } from '@/constant';
import platform from '@/shared/utils/platform';
import { sortLoanUsageListByCapitalCode } from '@/shared/utils/sortLoanUsageList';
import type {
  getCalRepayPlans,
  StateType,
  FullAmountCouponType,
} from './types';
import { QuoteLimiteEnum, ReplanResultType } from './types';
import {
  formatChannelList,
  getProductName,
  USER_SCENE_CONFIG_MAP,
  NORMAL_SCENE_CONFIG,
} from './product';
import {
  getEquitySelected, getMaxLoanAmount, getPricingTypeByProductId, updatePricingTypeMap,
  getDefaultSelectedCoupon,
} from './utils';
import marketingAmountStore from './marketingAmountStore';
import discountModuleStore from './discountModuleStore';
import couponDiscountRateStore from './couponDiscountRateStore';
import applyOptimizeStore from './applyOptimizeStore';
import { isCanBuyRightRateType } from './const';

/** 后面收到notice组件内部 */
const getNoticeByApplyLimit = (applyLimit: ChannelApplyLimitEnum): string => {
  switch (applyLimit) {
    case ChannelApplyLimitEnum.LOAN_AFTER_PAYMENT:
      return '本渠道需还清借款后才能继续提现，建议您取出全部额度';

    case ChannelApplyLimitEnum.BIND_CARD_ONCE:
      return '应放款机构的要求，本产品建议只绑定一张银行卡';

    case ChannelApplyLimitEnum.ONCE_LIMIT:
      return '本渠道仅支持提现一次，建议一次性取出所有额度';

    case ChannelApplyLimitEnum.ALL_LIMIT:
      return '本产品仅支持全部提现';

    case ChannelApplyLimitEnum.NO_LIMIT:
    default:
      return '';
  }
};
/** 通过渠道id+资方id，创建key */
export const generateProductKey = (productId: string, capitalCode?: string): string => `${productId}${capitalCode ? `__${capitalCode}` : ''}`;

/** 是否提现订单提交中 */
let isApplySubmitting = false;
/** 是否在请求自营限额流程实验结果 */
let isRequestingAutrakyProcessExpRes = false;

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    notice: '',
    channel: undefined,
    channelList: [],
    optimizationNoAvailableProduct: false,
    scene: UserSceneEnum.NORMAL,
    sceneConfig: NORMAL_SCENE_CONFIG,
    product: undefined,
    productList: {},
    brokenAmount: '',
    canCancel: false,
    brokenProductId: '',
    amount: '',
    maxAmount: '',
    period: undefined,
    periodList: [],
    /** 借款试算返回的多资方 */
    fundListData: undefined,
    /** 当前选择的借款试算资方index */
    currentCheckFundIndex: 0,
    planListData: undefined,
    /** 还款计划-是否查询中 */
    loadingCalcRepayPlans: false,
    bankCard: undefined,
    couponList: [],
    coupon: undefined,
    /** 满额提现券信息 */
    fullAmountCoupon: {
      amount: '',
      needSelect: false,
      experimentResult: undefined,
    },
    usage: undefined,
    usageList: [],
    isDynamicContracts: false,
    contracts: [],
    creditContracts: [],
    needTradePasswd: false,
    tradePasswdToken: '',
    submitResult: undefined,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    loanConfigCity: Object.create(null),
    iSalipaySignSuccess: false,
    isBankCardUsed: {
      cardNo: '',
      isBind: true,
      productId: 0,
    },
    isCouponVisible: false,
    needSupplyMaterials: false,
    // 还款方式
    repaymentModel: '',
    // 还款试算-无资金路由
    replanResultErrMsg: false,
    // 可提现渠道abtest测试
    channelGrayTest: 0,
    /** 获取提现渠道列表返回的需透传内容 */
    channelExtendObj: {},
    // 可提现渠道总金额
    totalAvailableAmount: 0,
    checkType: '',
    multiChannelRepayment: '',
    // 验证码
    smsCode: '',
    lastApplyResult: '',
    // loading状态
    isVisibleLoading: false,
    checkId: '',
    // 度小满查询申请状态
    queryRealTimeStatus: null,
    // 度小满-查询未完成订单
    orderCheckListDxm: null,
    /** 试算接口调用时存储的时间戳 */
    calcRepayPlansTime: 0,
    /** h5活体成功以后 取值 */
    h5BioSuc: false,
    couponId: '',
    payType: CardBindTypeEnum.BANK,
    /** 权益校验数据 */
    rightCheckStatus: null,
    /** 是否通联二次绑卡校验 */
    allinPayCheck: false,
    /** 双融担模式，用户选择的资方 */
    selectRightInCapital: null,
    /** 双融担tab(资方)切换，对应银行卡是否变更 */
    selectCapitalChangedForBank: false,
    /** 防诈骗配置 */
    antiFraudConfig: null,
    showHowToRepay: false,
    repayOnSchedule: false,
    /** 是否需要加载资源弹窗，首次计算完提现期数, 期数是提现金额计算完毕后计算的 */
    needLoadingAdvPopup: false,
    isUpgradeChecked: false,
    shouldInitSpeedState: false,
    /** 是否预授信召回挽留流程 */
    recallPreCredit: false,
    /** 自营流程实验结果 */
    autrakyProcessExpRes: AutrakyProcessExpEnum.DEFAULT,
    /** 是否24+单通道可换普通通道场景 */
    is24NeedRightAndNormalIs36: false,
    /** 标记当前自营产品是否需要购买臻享卡 */
    autrakyZhenxiangCardState: {
      clickedSubmitProductIds: [],
      autrakyNeedBuyZhenxiangCard: false,
    },
    /** 提额入口相关状态 */
    tempAmountEntry: {
      showEntry: undefined,
      clicked: false,
      applying: false,
      showSuccessPopup: false,
    },
  },

  getters: {
    /**
     * 当前提现产品是否需要购买权益
     * 1. 自营8180 取值来自资产接口 （f.zmoney.withdraw.channels）
     * 2. 非自营8180的产品由前端调用权益接口（f.marketing.rights.package.showRuleCheck）获取
    */
    needBuyRight(state: StateType) {
      const { channel, rightCheckStatus } = state;

      if (channel && channel.disabled !== ChannelDisableEnum.AVAILABLE) {
        return false;
      }

      const { capitalList = [] } = channel || {};
      const isDoubleIntegrateMode = !!(capitalList && capitalList.length);
      const doubleIntegrateHasRight = !!(isDoubleIntegrateMode && capitalList[0].needRight);

      // 自营 8180 直接通过needRight来判断
      if (channel
        && channel.productId === ProductCommodity.AUTARKY_B_COMMODITY) {
        return isDoubleIntegrateMode ? doubleIntegrateHasRight : !!channel.needRight;
      }

      // 联营导流，基于 canPurchase 判断，默认不需要购买权益
      // 如果 is24NeedRightAndNormalIs36 为 true，也需要购买权益
      return Boolean(rightCheckStatus?.canPurchase)
        || Boolean(state.is24NeedRightAndNormalIs36);
    },
    isAutrakyBuyRight(state: StateType) {
      const { channel } = state;
      const { capitalList = [] } = channel || {};
      const [capitalFirstList] = capitalList;
      const canBuyRightRateType = isCanBuyRightRateType(capitalFirstList?.rateType);
      // 如果capitalList有权益资方 & length>1, 此时判断是否需要购买臻享会员
      return canBuyRightRateType
              && capitalList?.length
              && capitalFirstList?.needRight;
    },
    /** 是否需要购买臻享会员 ** */
    needBuyZhenxiangMember(state: StateType, getters) {
      const { channel } = state;
      /** 如果disabled=false, 默认不需要购买 */
      if (channel && channel.disabled !== ChannelDisableEnum.AVAILABLE) {
        return false;
      }
      const { capitalList = [] } = channel || {};
      const [capitalFirstList] = capitalList;
      // 如果capitalList有值 & length>1, 此时判断是否需要购买臻享会员
      return getters.isAutrakyBuyRight
              && capitalFirstList?.packageType === RightPackageTypeEnum.ZHEN_XIANG_MEMBER_CARD;
    },
    /** 是否需要购买臻享卡 ** */
    needBuyZhenxiangCard(state: StateType, getters) {
      const { channel } = state;
      /** 如果disabled=false, 默认不需要购买 */
      if (channel && channel.disabled !== ChannelDisableEnum.AVAILABLE) {
        return false;
      }
      const { capitalList = [] } = channel || {};
      const [capitalFirstList] = capitalList;
      // 如果capitalList有值 & length>1, 此时判断是否需要购买臻享会员
      return (getters.isAutrakyBuyRight
              && channel?.productId === ProductCommodity.AUTARKY_B_COMMODITY
              && capitalFirstList?.packageType === RightPackageTypeEnum.CHANNEL_B)
              || (
                !!getters.needBuyRight
                && channel?.productId !== ProductCommodity.AUTARKY_B_COMMODITY
              );
    },
    /** 双通道用户，是否默认选择 "普通权益" */
    isDoublePassageDefaultSelectNormal(state: StateType): boolean {
      const { capitalList = [], defaultSelectCapitalIndex = 0 } = state.channel || {};
      const capital = capitalList[defaultSelectCapitalIndex];

      /** 若服务端返回defaultSelectCapitalIndex错误，则默认选择有权益资方 */
      return capital ? !capital.needRight : false;
    },
    /** 是否为双承接模式 */
    isDoublePassagewayPattern(state: StateType): boolean {
      const { capitalList = [] } = state.channel || {};
      const [firstCapital, secondCapital] = capitalList;

      // 双承接模式条件：第一家资方有权益，第二家资方无权益
      return firstCapital
        && firstCapital.needRight
        && secondCapital
        && !secondCapital.needRight;
    },
    /** 是否为24权益+纯24双承接模式 */
    isDoublePassagewayPattern24RightsAndPure24(state: StateType): boolean {
      const { capitalList = [] } = state.channel || {};
      const [capitalFirst, capitalSecond] = capitalList;
      return capitalFirst?.rateType === RateTypeEnum.CAPITAL_TYPE_24
        && capitalFirst?.needRight
        && capitalSecond?.rateType === RateTypeEnum.CAPITAL_TYPE_24;
    },
    /** 是否需要购买超级会员 */
    needBuySuperMember(state: StateType) {
      const { channel } = state;
      return channel
      && channel.capitalList
      && channel.capitalList.length
      && channel.capitalList[0].showSuperMember;
    },
    /** * 当前还款方式 */
    currentRepayType(state: StateType) {
      const currentProductRepayMode = state.product?.productInfo?.repaymentModel || [];
      const currentRepaymentLength = currentProductRepayMode.length;
      if (!state.multiChannelRepayment) {
        return currentRepaymentLength && currentProductRepayMode[0];
      }
      return state.multiChannelRepayment;
    },
    /** 构建提现申请的扩展字段对象 */
    submitExtendObj(state: StateType, getters): Record<string, unknown> {
      const { channelExtendObj, channel, is24NeedRightAndNormalIs36 } = state;
      const { isDoublePassagewayPattern, isDoublePassagewayPattern24RightsAndPure24 } = getters;

      const extendObj: Record<string, unknown> = { ...channelExtendObj };

      // 命中权益用的降价标签
      if (channel?.userLoanRate24ZyUsr) {
        extendObj.userLoanRate24ZyUsr = channel.userLoanRate24ZyUsr;
      }

      // 双承接的模式下，看是否需要显示限额弹窗
      if (isDoublePassagewayPattern || isDoublePassagewayPattern24RightsAndPure24) {
        const firstCapitalRateType = channel?.capitalList[0]?.rateType;

        extendObj.distributionMode = firstCapitalRateType === RateTypeEnum.CAPITAL_TYPE_20
          ? QuoteLimiteEnum.DUAL_CHANNEL_20 : QuoteLimiteEnum.DUAL_CHANNEL;
        extendObj.capitalType = state.channel?.rateType;
      }

      // 设置轻资产承接模式
      if (is24NeedRightAndNormalIs36) {
        extendObj.distributionMode = QuoteLimiteEnum.MIX_DUAL_CHANNEL;
      }

      return extendObj;
    },
    /** 是否可以显示满额提现券标签(金额小于最大金额&有满额提现券&命中实验) */
    showFullAmountCouponTag(state: StateType): boolean {
      return !!(state.fullAmountCoupon.amount
        && Number(state.amount) < Number(state.maxAmount))
        && state.fullAmountCoupon.experimentResult === ABResEnum.HINT;
    },

    /** 是否需要选中满额提现券 */
    needSelectFullAmountCoupon(state: StateType): boolean {
      return state.fullAmountCoupon.needSelect
        && !!(state.fullAmountCoupon.amount)
        && Number(state.amount) === Number(state.maxAmount);
    },
    /** 券模块禁用 */
    isCouponModuleDisabled(state: StateType, getters): boolean {
      const { showSpeed } = store.state.loanRight;
      return getters.isCouponLimitExperiment && showSpeed === false;
    },
    /** 券模块展示 */
    showCouponModule(state: StateType, getters): boolean {
      const { couponList } = state;
      return couponList.length > 0 && !getters.isCouponModuleDisabled;
    },
    /** 是否展示提额入口（接口返回true 且 未点击过） */
    shouldShowTempAmountEntry(state: StateType): boolean {
      return state.tempAmountEntry.showEntry === true && !state.tempAmountEntry.clicked;
    },
  },

  actions: {
    initUriQueryContext(
      context: ActionContext<StateType, RootStateType>,
    ) {
      context.dispatch(UPDATE_STATE_TYPE, {
        recallPreCredit: Boolean(URLUtils.getQueryMap().recallPreCredit),
      });
    },
    // 度小满-查询是否存在未完成订单
    async getOrderCheck(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const { channel } = context.state;
        const productId = channel && channel.productId;
        if (productId) {
          context.dispatch(UPDATE_STATE_TYPE, {
            orderCheckListDxm: await orderCheck(productId),
          });
        }
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },
    // 度小满-终止申请
    async orderUpdateFailed(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const { channel } = context.state;
        const productId = channel && channel.productId;
        if (productId) {
          await orderUpdateFailed(productId);
        }
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },
    // 度小满-第二次借款申请
    async orderApply(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const { channel } = context.state;
        const productId = channel && channel.productId;
        if (productId) {
          await orderApply(productId);
        }
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },
    // 度小满-借款实时查询
    async orderQueryRealTime(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const { submitResult, channel } = context.state;
        if (submitResult) {
          const { loanOrderId } = submitResult;
          const productId = channel && channel.productId;
          if (productId) {
            context.dispatch(UPDATE_STATE_TYPE, {
              queryRealTimeStatus: await orderQueryRealTime({ loanOrderId, productId }),
            });
          }
        }
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },
    // 银行卡是否可用
    async takeBankCardIsUsed(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        userBankCardId: string;
        productId: string;
      },
    ): Promise<void> {
      try {
        context.dispatch(UPDATE_STATE_TYPE, {
          isBankCardUsed: await isBankCardUsed({
            userBankCardId: payload.userBankCardId || '',
            productId: payload.productId || '',
            cardUsage: 'PAY',
          }),
        });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },

    // 获取需要定位的配置渠道
    async getConfigCity(
      context: ActionContext<StateType, RootStateType>,
      payload: { commodityId: string },
    ): Promise<void> {
      try {
        const { commodityId } = payload;
        const parmams = {
          commodityId: commodityId || '0',
          keys: ['gpsLoanDepend'],
        };
        context.dispatch(UPDATE_STATE_TYPE, {
          loanConfigCity: await getConfigCityItems(parmams),
        });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },

    // 获取可提现渠道列表
    async getChannelList(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        productId?: string;
      },
    ): Promise<void> {
      try {
        const { recallPreCredit } = context.state;
        const {
          productCreditResultList: channelList,
          gray: channelGrayTest,
          totalAvailableAmount,
          optimizationNoAvailableProduct,
          scene,
          extendObj: channelExtendObj,
        } = await getChannelList(pageSceneEnum.APPLY_PAGE, recallPreCredit);
        const makeChannelList = formatChannelList(channelList);

        // 提现失败结果页面可能会选择特定的渠道重新提现
        const selectedChannel = payload.productId
          ? makeChannelList.find((item) => item.productId === payload.productId
          && item.disabled === ChannelDisableEnum.AVAILABLE)
          : undefined;

        const channel = selectedChannel || makeChannelList[0] || null;

        const sceneConfig = USER_SCENE_CONFIG_MAP[scene] || NORMAL_SCENE_CONFIG;

        context.dispatch(UPDATE_STATE_TYPE, {
          channelList: makeChannelList,
          channelGrayTest,
          channelExtendObj,
          channel,
          totalAvailableAmount: Number(totalAvailableAmount),
          optimizationNoAvailableProduct,
          scene,
          sceneConfig,
          selectRightInCapital: channel.capitalList
            ? channel.capitalList[channel.defaultSelectCapitalIndex || 0] : null,
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 双融担权益Tab切换时，更新对应的资方信息(还款期限、还款计划)
    async changeSelectCapitalForRight(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        productCapitalItem: ProductCapitalItem,
      },
    ): Promise<void> {
      const { productCapitalItem } = payload;
      const { channel, channelList, recallPreCredit } = context.state;
      const { productId, capitalCode } = payload.productCapitalItem;

      try {
        const newChannel = {
          ...channel,
          ...productCapitalItem,
          productName: getProductName(channel, productCapitalItem),
        };

        /** 更新用户选择的资方信息 */
        context.dispatch(UPDATE_STATE_TYPE, {
          channelList: channelList.map((it) => (
            /** 线上相同渠道有可能存在两个授信额度记录，通过creditApplyId区分开: https://metis2.hellobike.cn/browse/SKXDZC-8532 */
            it.productId === newChannel.productId && it.creditApplyId === newChannel.creditApplyId
              ? newChannel : it)),
          channel: newChannel,
        });

        try {
          const product = await getApplyProduct({
            productId,
            capitalCode,
            recallPreCredit,
            fundRouteResponseId: newChannel.fundRouteResponseId,
          });
          await context.dispatch(UPDATE_STATE_TYPE, {
            isVisibleLoading: true,
            product,
            selectCapitalChangedForBank: true,
            showHowToRepay: !!product?.productInfo?.repayOnSchedule,
            /** 怎么还模块显示时，默认按期还款 */
            repayOnSchedule: !!product?.productInfo?.repayOnSchedule,
          });
          // 切换资方时，重新计算最大可提现金额
          context.dispatch('getMaxAmount', {
            notUpdateAmount: true,
          });
        } catch {
          /** 重置"银行卡账号、久期设置"数据 */
          await context.dispatch(UPDATE_STATE_TYPE, {
            isVisibleLoading: true,
            product: undefined,
            selectCapitalChangedForBank: false,
            showHowToRepay: false,
            repayOnSchedule: false,
          });
        }

        return Promise.resolve();
      } catch (err) {
        ubtSendError(err as Error);

        return Promise.reject();
      }
    },
    // 获取对应渠道的产品信息
    async selectApplyChannel(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        channel: ApplyChannelData;
      },
    ): Promise<void> {
      try {
        const {
          productId, creditApplyId, capitalCode, disabled,
        } = payload.channel;
        const { productList, recallPreCredit } = context.state;

        /** 选中 银河渠道 时，根据接口去获取资方绑卡类似，是支付宝签约还是银行卡 */
        if (isNewYhChannel(productId)) {
          const parm = {
            creditApplyId,
          };
          const { payType } = await alipayOrBank(parm);

          context.dispatch(UPDATE_STATE_TYPE, {
            payType,
          });
        } else {
          context.dispatch(UPDATE_STATE_TYPE, {
            payType: CardBindTypeEnum.BANK,
          });
        }
        // 切换渠道时，清空定价类型缓存
        updatePricingTypeMap(payload?.channel?.productId, undefined);
        // 切换渠道时，清空满额提现券信息
        context.dispatch('updateFullAmountCoupon', {
          amount: '',
          needSelect: false,
        });

        // 更新选中的渠道，并重置needSupplyMaterials状态
        context.dispatch(UPDATE_STATE_TYPE, {
          channel: payload.channel,
          needSupplyMaterials: false,
          brokenProductId: '', // 渠道切换时，中断渠道 id 重置
        });
        const key = generateProductKey(productId, capitalCode);

        // 只有缓存中不存在数据&渠道可用的时候采取请求更新
        if (!productList[key] && disabled === ChannelDisableEnum.AVAILABLE) {
          let newProductInfo;
          // 获取渠道信息接口抛出异常，catch，并清空当前信息
          try {
            newProductInfo = await getApplyProduct({
              productId,
              capitalCode,
              recallPreCredit,
              fundRouteResponseId: payload.channel.fundRouteResponseId,
            });
          } catch {
            newProductInfo = undefined;
          }

          context.dispatch(UPDATE_STATE_TYPE, {
            productList: {
              ...productList,
              [key]: newProductInfo,
            },
            isVisibleLoading: true,
            currentCheckFundIndex: 0,
          });
        }

        // 设置选中的提现产品 以及 默认数据
        context.dispatch(UPDATE_STATE_TYPE, {
          product: context.state.productList[key],
          notice: getNoticeByApplyLimit(payload.channel.applyLimit),
          showHowToRepay: !!context.state.productList[key]?.productInfo?.repayOnSchedule,
          /** 如果显示怎么还，默认按期还款 */
          repayOnSchedule: !!context.state.productList[key]?.productInfo?.repayOnSchedule,
        });

        // 切换渠道时，获取自营流程实验结果
        context.dispatch('getAutrakyProcessExpRes');

        // 切换渠道时，获取营销系数实验结果
        context.dispatch('getMarketingCoefficientABRes');

        // 切换渠道时
        // 1.将可以重新初始化极速状态置为可用
        // 2.将联营臻享卡相关状态置为null，避免切换闪动
        context.dispatch(UPDATE_STATE_TYPE, {
          shouldInitSpeedState: true,
          rightCheckStatus: null,
        });

        context.dispatch('initSpeedStatus');

        store.dispatch('loanRight/$updateState', {
          // 清空上一次超会及臻享卡信息，防止ab样式及试算接口返回较慢情况下的页面展示抖动
          superMemberInfo: null,
          isSuperMemberCheck: false,
        });

        context.dispatch('getMaxAmount');
        context.dispatch('getLoanUsageList');
        context.dispatch('checkNeedTradePasswd');
        if (productId === DUXIAOMAN_COMMODITY) {
          await context.dispatch('getOrderCheck');
        }
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 获取渠道的最大可提现金额
    async getMaxAmount(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        // 是否不更新amount，用于切换资方通道时，不更新amount
        notUpdateAmount?: boolean;
      },
    ): Promise<void> {
      try {
        const { product } = context.state;
        const isCouponModuleDisabled = context.rootGetters['applyProduct/isCouponModuleDisabled'];

        if (product) {
          const amount = getMaxLoanAmount(product, isCouponModuleDisabled);

          if (payload?.notUpdateAmount) {
            context.dispatch(UPDATE_STATE_TYPE, {
              maxAmount: amount,
            });
          } else {
            context.dispatch(UPDATE_STATE_TYPE, {
              amount,
              maxAmount: amount,
            });
          }
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    /** 校验权益是否可购买规则 */
    async checkRightRuler(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        maxAmount: number,
        rateValue: number,
        productId: string,
        planListData: CalcPlanListData,
      },
    ): Promise<void> {
      // 如果不存在产品信息 或者 为自营b产品则不用调用checkrule接口
      if (payload?.productId === ProductCommodity.AUTARKY_B_COMMODITY) {
        return Promise.resolve();
      }

      const { pricingType } = payload.planListData || {};

      try {
        // 调用权益是否可见接口
        const rightCheckStatus = (await getCheckResult({
          packageType: RightPackageTypeEnum.CHANNEL_B,
          productId: payload?.productId || '',
          source: RightPackageSourceEnum.LOAN,
          applyAmount: payload && String(payload.maxAmount),
          rateValue: payload?.rateValue,
          pricingType,
        }));
        context.dispatch(UPDATE_STATE_TYPE, {
          rightCheckStatus,
        });

        // 存在用户定价标签，且权益不可购买（大概率），则重新计算还款计划
        if (pricingType) {
          if (isReCalcRequiredForPricingType(pricingType) && !rightCheckStatus?.canPurchase) {
            context.dispatch('calcRepayPlans', {
              repayType: context.state.repaymentModel,
              currentAmount: context.state.amount,
              notNeedRightRulerCheck: true,
              equitySelected: EquitySelectedEnum.NO,
            });
            // reject 是为了上次试算方法不执行后续逻辑，因为这里已经执行了第二次试算
            return Promise.reject();
          }
        }
        return Promise.resolve();
      } catch {
        // 如果调用规则接口出错，直接默认不展示权益模块
        context.dispatch(UPDATE_STATE_TYPE, {
          rightCheckStatus: { canPurchase: false },
        });
        return Promise.resolve();
      }
    },

    // 计算可选择期限列表相关数据
    getPeriodData: debounce(
      async (
        context: ActionContext<StateType, RootStateType>,
        currentAmount: string,
      ): Promise<void> => {
        const { h5BioSuc } = context.state;
        // h5活体成功跳转回来，数据不清空
        if (!h5BioSuc) {
          // 调用前清空上一个渠道的试算数据
          context.dispatch(UPDATE_STATE_TYPE, {
            periodList: [],
            period: undefined,
          });
        }

        try {
          const { product, period } = context.state;
          const amount = Number(currentAmount || '0');

          if (product) {
            const periodData = product.productInfo
              && product.productInfo.periodRules.find(
                (item) => Number(item.min) <= amount && Number(item.max) >= amount,
              );

            if (periodData) {
              const periodList = periodData.periodRule.map((item) => ({
                ...item,
                value: item.period,
                unitName: PeriodTypeValue(item.periodUnit),
                label: `${item.period}${PeriodTypeValue(item.periodUnit)}`,
              }));
              context.dispatch(UPDATE_STATE_TYPE, {
                periodList,
                // 活体成功跳转场景，期数用原先的
                period: h5BioSuc ? period : periodList[0],
              });
            }
          }

          /** 这里的含义是：是否第一次计算期限，用于后续更新加载权益豁免广告位弹窗
           *  判断currentAmount与仓库中的maxAmount相等实际含义是
           *  确保用户第一次进入提现页，不受网络损耗导致amount值进行变更的影响
          */
          const { maxAmount, needLoadingAdvPopup } = context.state;
          if (currentAmount === maxAmount && !needLoadingAdvPopup) {
            context.dispatch(UPDATE_STATE_TYPE, {
              needLoadingAdvPopup: true,
            });
          }

          return Promise.resolve();
        } catch (error) {
          return Promise.reject(error);
        }
      },
      /**
       * 此处未改为300ms是因为以下原因
       * 1. 期数改变会调用试算接口，若内部逻辑处理为1ms，整体消耗时间则为301ms，超过了试算接口的300ms，还是会存在闪动问题
       * 2. 此处不存在接口调用，所以无需与https://hellobike.yuque.com/yxr77w/oyyz8n/erzzaewbphupyikg 中为解决试算接口问题报错问题保持一致
       * 3. 以后修改此处的debounce时间也不能超过试算设置时间
       */
      100,
    ),
    /** 每次试算之后实时去请求臻享卡、超级会员入口展示规则，核心是保证每次路由之后的利率不发生变化 */
    initRightState: async (
      context: ActionContext<StateType, RootStateType>,
      payload: {
        channel: ApplyChannelData;
        shouldInitSpeedState: boolean;
        maxAmount: number;
        planListData: CalcPlanListData;
      },
    ) => {
      // 24+权益单通道选择右侧轻资产场景，无需初始化权益入口数据
      if (context.state.is24NeedRightAndNormalIs36) {
        return;
      }
      const {
        channel, shouldInitSpeedState, maxAmount, planListData,
      } = payload;
      // 在渠道选择时触发购买权益规则同步调用
      // 这个只在非自营产品下触发，自营的臻享卡是否可购买是由资金路由接口返回
      if (channel?.productId !== ProductCommodity.AUTARKY_B_COMMODITY) {
        await context.dispatch('checkRightRuler', {
          maxAmount,
          rateValue: planListData?.rateValue,
          productId: channel?.productId,
          planListData,
        });
      }

      // 判断shouldInitSpeedState，标记只在切换渠道之后，初始化臻享卡状态
      // 核心要素是不希望这次试算后再去初始化臻享卡状态，防止覆盖用户操作
      if (shouldInitSpeedState) {
      // 根据渠道状态来初始化权益卡片tab状态
        context.dispatch('initSpeedStatus');
        context.dispatch(UPDATE_STATE_TYPE, {
          shouldInitSpeedState: false,
        });
      }

      // 再check超级会员是否展示
      store.dispatch('loanRight/needBuySuperMemberCheck', {
        channel,
        planListData,
      });
    },

    // 试算还款计划
    calcRepayPlans: debounce(
      async (
        context: ActionContext<StateType, RootStateType>,
        payload?: getCalRepayPlans,
      ): Promise<void> => {
        try {
          const {
            period, product, coupon, bankCard, channel,
          } = context.state;
          const { isCouponModuleDisabled } = context.getters;

          context.dispatch(UPDATE_STATE_TYPE, {
            repaymentModel: (payload && payload.repayType) || '',
          });

          const productId = product && product.productInfo && product.productInfo.productId;
          const currentAmount = payload && payload.currentAmount;
          if (currentAmount && period && product) {
            // 试算判断，试算下一次之前，如果之前试算有值，先清空之前的试算数据
            if (context.state.planListData) {
              context.dispatch(UPDATE_STATE_TYPE, {
                planListData: undefined,
              });
            }

            const time = new Date().getTime();
            // 请求前 存储当前时间戳
            context.dispatch(UPDATE_STATE_TYPE, {
              calcRepayPlansTime: time,
              loadingCalcRepayPlans: true,
            });

            await calcRepayPlans({
              bankCardId: (bankCard && bankCard.bankId) || '',
              amount: Number(currentAmount),
              period: Number(period.value),
              repayType: (payload && payload.repayType) || '2',
              productId:
                (productId) || '',
              couponId: (!isCouponModuleDisabled && coupon && coupon.couponId) || '',
              capitalCode: (channel && channel.capitalCode) || '',
              fundRouteResponseId: channel?.fundRouteResponseId || '',
              externalCouponId: (!isCouponModuleDisabled && coupon && coupon.externalCouponId) || '',
              /** 当前产品是否支持按期还款 传对应值，去获取按期还款利率和优惠金额 */
              repayOnSchedule: !!product?.productInfo?.repayOnSchedule,
              /** 是否需要购买权益，轻资产24+权益时根据用户是否展示权益购买以及是否勾选权益购买 */
              equitySelected: getEquitySelected({
                productId: String(productId),
                equitySelected: payload?.equitySelected,
                hasInitRightStatus: context.state.rightCheckStatus
                && typeof (context.state.rightCheckStatus?.canPurchase) === 'boolean',
              }),
              // 传默认逻辑标记版本
              bizVersion: CALC_REPAYPLANS_VERSION,
              // 是否需要计算优惠券折扣率
              calcCouponDiscountRate: context.rootGetters['applyProduct/showCouponDiscountRateExperiment'],
            })
              .then(async (planListData) => {
                // 更新试算维度的数据
                updatePricingTypeMap(channel?.productId, planListData?.pricingType);

                const { calcRepayPlansTime, currentCheckFundIndex } = context.state;
                // 获取到数据后，判断当前异步请求时间和存储的时间戳是否一致 来判断是否是最后一次发起的请求
                if (time === calcRepayPlansTime) {
                  const { shouldInitSpeedState, maxAmount } = context.state;
                  // 当调用权益时指定无需重新获取权益的定价类型时，不请求
                  if (!payload?.notNeedRightRulerCheck) {
                    // 权益入口初始化数据调用
                    await context.dispatch('initRightState', {
                      channel: product?.productInfo,
                      shouldInitSpeedState,
                      maxAmount,
                      planListData,
                    });
                  }

                  // 多资方试算列表
                  if (planListData.fundList && planListData.fundList.length > 0) {
                    const { fundList } = planListData;
                    let currentPlanListData;
                    let currentFundIndex;
                    // 不存在当前已选中的资方，说明是首次请求试算接口
                    if (!currentCheckFundIndex) {
                      // 到第一个资方的试算信息作为弹窗展示内容
                      [currentPlanListData] = fundList;
                      currentFundIndex = 0;
                    } else {
                      // 非首次试算
                      // 查询所有资方试算列表中与当前所选中资方匹配的资方信息，赋值给弹窗展示对象
                      currentPlanListData = fundList[currentCheckFundIndex];
                      currentFundIndex = currentCheckFundIndex;
                    }

                    // 存储所有资方的试算信息
                    context.dispatch(UPDATE_STATE_TYPE, {
                      planListData: currentPlanListData,
                      currentCheckFundIndex: currentFundIndex,
                      fundListData: fundList,
                      loadingCalcRepayPlans: false,
                    });
                  } else {
                    context.dispatch(UPDATE_STATE_TYPE, {
                      planListData,
                      loadingCalcRepayPlans: false,
                    });
                  }

                  const {
                    calcResult, calcMsg, userCalcMsg, userCalcCode,
                  } = planListData || {};
                  // 针对当前 试算失败的情况，特殊处理 toast展示错误信息；
                  if (calcResult === ReplanResultType.REPLAN_FAILED) {
                    if (Number(userCalcCode) === ResponseCodeEnum.ALIPAY_36_GUIDE) {
                      ubtSetupModuleExpose('finance_loan_apply_alipay36Guide', {
                        scene: 'alimini_36_guide',
                      });
                      Dialog.alert({
                        title: '温馨提示',
                        message: '请前往"哈啰APP首页-借钱"进行提现',
                        confirmButtonText: '确定',
                        closeable: false,
                      }).then(() => {
                        ubtSetupPageButton('finance_loan_apply_alipay36Guide_agree', {
                          scene: 'alimini_36_guide',
                        });
                        router.replace({
                          path: RouterV2Enum.alipayCreditGuide,
                          query: {
                            amount: maxAmount,
                          },
                        });
                      });
                      return;
                    }

                    const calcResultErrorMsg = userCalcMsg || '系统维护中，请稍后尝试';
                    Toast.clear();
                    Toast(calcResultErrorMsg);
                    if (calcMsg) {
                      /** 上报真实的错误原因 js错误 */
                      ubtSendError(`calc error: ${calcMsg}`);
                    }
                  }
                  /**
                   * 试算请求之后，重新刷新臻享卡详情数据
                   * 1. 默认选择权益通道模式，仅在极速状态下，去重新请求臻享卡数据
                   * 2. 非默认选择模式(新)，不论选择极速、还是普通权益，都需重新请求臻享卡数据
                   */
                  const { showSpeed } = store.state.loanRight;

                  if (context.getters.needBuyRight && showSpeed) {
                    await store.dispatch('loanRight/getRightSummaryData', {
                      planListData,
                    });
                  }

                  /** 上报试算结果 */
                  ubtSetupModuleExpose(
                    'finance_loan_req_response',
                    {
                      action: 'f.loan.bill.calc',
                      result: JSON.stringify({
                        rateValue: planListData?.rateValue,
                        rateType: planListData?.rateType,
                        pricingType: planListData?.pricingType,
                        trialFlowNo: planListData?.trialFlowNo,
                        timestamp: new Date().getTime(),
                      }),
                    },
                  );
                }
              }).catch((error) => {
                const { calcRepayPlansTime } = context.state;
                // 获取到数据后，判断当前异步请求时间和存储的时间戳是否一致 来判断是否是最后一次发起的请求
                if (time === calcRepayPlansTime) {
                  context.dispatch(UPDATE_STATE_TYPE, {
                    planListData: undefined,
                    fundListData: undefined,
                    loadingCalcRepayPlans: false,
                  });

                  if (
                    Number(error?.code) === ResponseCodeEnum.CHECK_DIALOG_CONFIRM
                  ) {
                    context.dispatch(UPDATE_STATE_TYPE, {
                      replanResultErrMsg: true,
                    });
                  }

                  if (
                    Number(error?.code)
                    === ResponseCodeEnum.NEED_SUPPLY_BEFORE_CALC_REPAYPLAN
                  ) {
                    ubtSendError('need_supply_before_calc_repayplan');
                  }
                }
              });
          }

          return Promise.resolve();
        } catch (error) {
          return Promise.reject(error);
        }
      },
      /**
       * 此处的延迟，是为了防止用户快速点击，导致多次请求
       * 300ms时间的限制与https://hellobike.yuque.com/yxr77w/oyyz8n/erzzaewbphupyikg  问题处理有关
       * 由于在金额检验设置300ms延迟会造成切换渠道试算闪动问题（两边都调用了getPeriodData，而期数改变也会调用试算接口）
       * 所以将金额校验处300ms延迟移除，此处的delayTime参数改为300
       * */
      300,
    ),

    // 更新满额提现券信息
    updateFullAmountCoupon(
      context: ActionContext<StateType, RootStateType>,
      payload: Partial<FullAmountCouponType>,
    ) {
      context.dispatch(UPDATE_STATE_TYPE, {
        fullAmountCoupon: {
          ...context.state.fullAmountCoupon,
          ...payload,
        },
      });
    },

    // 确认满额提现
    confirmFullAmount(
      context: ActionContext<StateType, RootStateType>,
    ) {
      context.dispatch('getMaxAmount');
      context.dispatch('updateFullAmountCoupon', {
        needSelect: true,
      });
    },

    // 获取优惠券列表
    getCouponList: debounce(
      async (
        context: ActionContext<StateType, RootStateType>,
      ): Promise<void> => {
        try {
          const {
            product, couponId, amount, period, externalCouponId,
            channel,
            multiChannelRepayment,
            maxAmount,
          } = context.state;

          const currentProductRepayMode = product?.productInfo?.repaymentModel || [];

          const currentRepayType = multiChannelRepayment
          || (currentProductRepayMode.length > 0 && currentProductRepayMode[0]);
          const repayType = repayPlanModelType[currentRepayType as string]
          || repayPlanModelType.EPAI;
          // 过滤出可用的优惠券
          const couponList = ((await getRuleSetCouponList({
            maxAmount: Number(maxAmount) || 0,
            loanAmount: Number(amount) || 0,
            productId: (product && product.productInfo) ? product.productInfo.productId : '',
            pageIndex: 1,
            pageSize: 1000,
            selCurrentPeriod: Number(period && period.value) || 0,
            repayType,
            capitalCode: channel?.capitalCode || '',
            repayOnSchedule: product?.productInfo?.repayOnSchedule || false,
          }))).filter((item) => !!item.availability);

          /** 哈啰app环境内获取满额场景下的满额提现券信息 */
          if (platform.isEasyBike && Number(amount) === Number(maxAmount)) {
            /** 是否存在可用满额提现券 */
            const fullAmountCouponInfo = couponList.find(
              (item) => !!item.ruleInfo?.fullLoanAmount,
            );
            if (fullAmountCouponInfo?.amount) {
              context.dispatch('updateFullAmountCoupon', {
                amount: fullAmountCouponInfo?.amount,
              });
              /** 哈啰app内&未获获取过实验结果，请求伽利略并存储实验结果 */
              if (!context.state.fullAmountCoupon.experimentResult) {
                const { result } = await getGalileoAb().getAbResCatchError('202508261339175627', '202508261339175737');
                context.dispatch('updateFullAmountCoupon', {
                  experimentResult: result,
                });
              }
            }
          }

          // 链接带 couponId 或 externalCouponId
          if (couponId || externalCouponId) {
            const coupon = couponList.find(
              (item) => item.couponId === externalCouponId || item.couponId === couponId,
            );
            if (coupon) {
              context.dispatch(UPDATE_STATE_TYPE, {
                coupon,
                couponList,
              });
            } else {
              context.dispatch(UPDATE_STATE_TYPE, {
                couponList,
              });
            }
          } else {
            let coupon;
            /** 默认选中类型优先级最高的券 */
            coupon = getDefaultSelectedCoupon(couponList);
            // 如果没有默认选中x天y元免息券，有满额提现券且需要选中，则选中满额提现券
            if (!coupon && context.getters.needSelectFullAmountCoupon) {
              coupon = couponList.find(
                (item) => !!item.ruleInfo?.fullLoanAmount,
              );
            }
            context.dispatch(UPDATE_STATE_TYPE, {
              coupon,
              couponList,
            });
          }

          // 优惠券获取后，获取优惠模块优化实验结果
          context.dispatch('getDiscountModuleExperimentRes');

          return Promise.resolve();
        } catch (error) {
          context.dispatch(UPDATE_STATE_TYPE, {
            couponList: [],
            coupon: undefined,
          });
          return Promise.reject(error);
        }
      },
      /**
       * 与calcRepayPlans的debounce时间保持一致
       */
      300,
    ),

    // 查询支付宝签约状态
    async checkAlipayStatus(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const productId = YINHE_COMMODITY;
        context.dispatch(UPDATE_STATE_TYPE, {
          iSalipaySignSuccess: await checkAlipayStatus(productId),
        });
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(e);
      }
    },

    // 获取借款用途列表
    async getLoanUsageList(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const usageList = await getLoanUsageList();

        // 使用公共方法对借款用途列表进行排序
        const capitalCode = context.state.channel?.capitalCode;
        const sortedUsageList = sortLoanUsageListByCapitalCode(capitalCode, usageList);

        context.dispatch(UPDATE_STATE_TYPE, {
          usageList: sortedUsageList,
          usage: sortedUsageList[0],
        });

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 获取多资方协议列表
    async multiCapitalContracts(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      const {
        channel,
        amount,
        usage,
        period,
        bankCard,
        submitResult,
      } = context.state;
      try {
        const list : Commodity[] = await getAgreementsByCommodities({
          commodityIds: channel ? [Number(channel.productId)] : [],
          type: ContractEnum.APPLY,
          capitalCode: (channel && channel.capitalCode) || '',
          orderId: (submitResult && submitResult.loanOrderId) || '',
          amount: Number(amount) * 100 || undefined,
          period: Number(period && period.period) || undefined,
          bankCardId: (bankCard && bankCard.bankId) || '',
          loanUsage: (usage && usage.code) || '',
          fundRouteResponseId: channel?.fundRouteResponseId || '',
          /** 扩展字段，透传给后端 */
          extendObj: context.getters.submitExtendObj,
        }, true);
        list.forEach((item) => {
          item.contractActive = 0;
          item.isExpand = false;
        });
        context.dispatch(UPDATE_STATE_TYPE, {
          isDynamicContracts: true,
          contracts: list,
        });
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 获取8180渠道授信协议信息
    async getAutarkyAgreements(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const commodity: Commodity[] = await getAgreementsByCommodities({
          commodityIds: [8180],
          type: ContractEnum.PREAPPLY,
        }, true);
        const { contractList = [] } = commodity[0];

        context.dispatch(UPDATE_STATE_TYPE, {
          creditContracts: contractList,
        });
        return Promise.resolve();
      } catch (error) {
        ubtSendError(error as Error);
        return Promise.reject();
      }
    },

    // 获取协议列表
    async getContracts(
      context: ActionContext<StateType, RootStateType>,
      payload: LoanAgreementPO & { showAllinPayContract: boolean },
    ): Promise<void> {
      try {
        const {
          amount,
          period,
          channel,
          usage,
          bankCard,
          submitResult,
          planListData,
        } = context.state;

        const { trialFlowNo } = planListData || {};

        if (channel) {
          const contractConfig = ProductContracts[Number(channel.productId)] || {};
          const { funderCode, showAllinPayContract } = payload;

          const params = {
            amount: `${Number(amount) * 100}` || '',
            period: String((period && period.period) || ''),
            commodityId: channel.productId,
            loanUsage: (usage && usage.code) || '',
            bankCardId: (bankCard && bankCard.bankId) || '',
            orderId: (submitResult && submitResult.loanOrderId) || '',
            /** 度小满流程改造，协议类型多变，支持动态传入 */
            type: payload.remarkType,
            // 欢太对接，获取贷前合同需要增加试算流水号
            trialFlowNo,
            capitalCode: (channel && channel.capitalCode) || '',
            showAllinPayContract,
            /** 是否需要购买权益，轻资产24+权益时根据用户是否展示权益购买以及是否勾选权益购买 */
            equitySelected: getEquitySelected({
              productId: String(channel.productId),
              hasInitRightStatus: true,
            }),
            pricingType: getPricingTypeByProductId(String(channel.productId)),
            /** 扩展字段，透传给后端 */
            extendObj: context.getters.submitExtendObj,
          };

          if (
            contractConfig.applyContracts
            && contractConfig.applyContracts.items
          ) {
            /**
             * 银河相关提现协议本地维护，现阶段不需要调接口获取
             * 银河不同资方，获取展示的协议不同
            */
            // 默认杭银
            let contracts = contractConfig.applyContracts.items;

            // 恒生资方
            if (funderCode === 'HENGSHENG') {
              contracts = contractConfig.applyContracts.spareItems as ContractItem[];
            }
            // 博能小贷资方
            if (funderCode === 'BONENGXIAODAI') {
              ubtSendError('bonengxiaodai should not be called');
            }
            // 芒果小贷资方  只在app4060新增资方
            if (funderCode === 'MANGGUOXIAODAI') {
              contracts = contractConfig.applyContracts.fourthItems as ContractItem[];
            }

            let contractsRes = contracts;

            if (showAllinPayContract) {
              const res = await getAllinpayContractUrl({
                ...params,
              });
              if (res) {
                contractsRes = contracts.concat(res);
              }
            }

            context.dispatch(UPDATE_STATE_TYPE, {
              isDynamicContracts: false,
              contracts: contractsRes,
            });
          } else {
            /**
             * 调接口获取协议
            */
            context.dispatch(UPDATE_STATE_TYPE, {
              isDynamicContracts: true,
              contracts: await getLoanContracts({
                ...params,
                repaymentModelName: RepaymentModel.EPAI,
              }),
            });
          }
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 判断是否需要交易密码
    async checkNeedTradePasswd(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      try {
        const { channel } = context.state;

        if (channel) {
          context.dispatch(UPDATE_STATE_TYPE, {
            needTradePasswd: await checkNeedTradePasswd(channel.productId),
          });
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 正常发起提现申请
    async submitOrderApply(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        smsCode?: string;
        defaultCouponId?: string;
        // TODO: 待优化，后续后端可根据 couponId 直接获取到 externalCouponId，无需前端透传
        defaultExternalCouponId?: string;
        lastApplyResult?: boolean;
        insuranceRelationNo?: string;
        loanApplyMarketReq: LoanApplyMarketReqType;
      },
    ): Promise<void> {
      try {
        const {
          amount,
          period,
          usage,
          channel,
          bankCard,
          coupon,
          checkType,
          planListData,
          checkId,
          repaymentModel,
          tradePasswdToken,
          payType,
          allinPayCheck,
          repayOnSchedule,
          contracts,
          isUpgradeChecked,
          recallPreCredit,
          is24NeedRightAndNormalIs36,
        } = context.state;
        const { isCouponModuleDisabled } = context.getters;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const { productId } = channel!;
        /** 查找是否返回的征信协议 */
        const hasCreditRouteChannel = contracts.filter((item) => item.creditRouteChannel);

        const {
          smsCode, defaultCouponId, lastApplyResult,
          insuranceRelationNo, defaultExternalCouponId,
          loanApplyMarketReq,
        } = payload;

        const isNewYhProduct = payType === CardBindTypeEnum.ALIPAY ? true : bankCard;

        // planListData使用!强制断言，如果为空，则抛出异常，不走后续提现流程
        // TODO:后续需要在调用提现接口前，前置校验提现必填参数，若没有则需要前端前置拦截并上报错误
        const {
          rateValue, rateType, trialFlowNo, amcFlag,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        } = planListData!;

        if (period && usage && channel && isNewYhProduct) {
          /** 提现逻辑单例
           * 提现接口请求发起但未拿到结果，不能再次请求接口
           */
          if (isApplySubmitting) return Promise.resolve();
          isApplySubmitting = true;

          const submitResult = await submitOrderApply(
            {
              amount,
              applyPeriod: period.period,
              applyPeriodUnit: period.periodUnit,
              loanUsage: usage.code,
              passwordToken: tradePasswdToken,
              productId: channel.productId,
              // 支付宝签约场景，不传银行卡 id, 解决从已绑卡渠道 切换到支付宝签约场景，传绑卡的 id，导致无法提现的问题
              userBankCardId: payType === CardBindTypeEnum.ALIPAY ? '' : bankCard && bankCard.bankId,
              smsCode: smsCode || '',
              couponId: (!isCouponModuleDisabled && coupon) ? coupon.couponId : defaultCouponId,
              externalCouponId: (!isCouponModuleDisabled && coupon)
                ? coupon.externalCouponId : defaultExternalCouponId,
              couponType: (!isCouponModuleDisabled && coupon) ? coupon.couponType : '',
              freeCouponType: (!isCouponModuleDisabled && coupon) ? coupon.freeCouponType : '',
              checkType: (smsCode) || (lastApplyResult) ? '' : checkType,
              // eslint-disable-next-line no-nested-ternary
              checkId: productId !== DUXIAOMAN_COMMODITY ? (checkId || '') : (!checkType && (!(smsCode) && !(lastApplyResult)) ? '' : (checkId || '')),
              interestDisplay: rateValue || 0,
              interestUnitDisplay: rateType || 'DAY',
              repaymentModel:
                RepayMentModelType[repaymentModel] || RepaymentModel.EPAI,
              insuranceRelationNo, // 投保回溯码
              trialFlowNo, // 试算流水号 翼支付和欢太花钱在用
              amcFlag,
              creditApplyId: channel.creditApplyId, // 试算流水号 ID
              /** 写死 银行卡类型，没有数字钱包的情况了，后续可以考虑下单这个字段，这次前端先发布，得先留着字段 */
              accountType: bankCard && AccountType.BANK,
              capitalCode: channel.capitalCode || '',
              routeMarket: channel.routeMarket,
              /** 传递上次透传的需要二次绑卡的需求 */
              allinPayCheck,
              repayOnSchedule,
              /** 征信路由渠道 */
              creditRouteChannel: (hasCreditRouteChannel[0]
                && hasCreditRouteChannel[0].creditRouteChannel
              ),
              fundRouteResponseId: channel.fundRouteResponseId,
              loanApplyMarketReq: {
                ...loanApplyMarketReq,
                packageType: is24NeedRightAndNormalIs36
                  ? undefined : loanApplyMarketReq.packageType,
              },
              loanWithCredit: isUpgradeChecked,
              loanWithCreditProductList: isUpgradeChecked ? ['8180'] : [],
              recallPreCredit,
              extendObj: context.getters.submitExtendObj,
            },
          );
          context.dispatch(UPDATE_STATE_TYPE, {
            submitResult,
            checkId: submitResult
              && submitResult.applyOrderCheckVo
              && submitResult.applyOrderCheckVo.checkId,
            allinPayCheck: submitResult
              && submitResult.applyOrderCheckVo
              && submitResult.applyOrderCheckVo.allinPayCheck,
          });
          isApplySubmitting = false;
          return Promise.resolve();
        }
        return Promise.reject(new Error('请选择期限以及放款银行卡'));
      } catch (error) {
        isApplySubmitting = false;
        // 异常的时候，下单订单结果数据重置, 避免watch submitResult 重复执行
        context.dispatch(UPDATE_STATE_TYPE, {
          submitResult: undefined,
        });
        return Promise.reject(error);
      }
    },

    // 发送提现短信验证码
    async sendApplySms(
      context: ActionContext<StateType, RootStateType>,
      smsSceneType: SmsSceneType,
    ): Promise<void> {
      try {
        const {
          channel, submitResult, bankCard, brokenProductId,
        } = context.state;

        if (channel && bankCard) {
          sendApplySms({
            // 中断订单短信验证码重新下发场景 传对应中断订单渠道 id
            commodityId: (smsSceneType && smsSceneType === SmsSceneType.BROKEN)
              ? (brokenProductId || channel.productId) : channel.productId,
            userBankCardId: bankCard.bankId,
            loanApplyId: submitResult ? submitResult.loanOrderId : '',
            checkId:
              submitResult && submitResult.applyOrderCheckVo
                ? submitResult.applyOrderCheckVo.checkId
                : '',
          });
        }

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 确认下单短信验证码
    async confirmApplySms(
      context: ActionContext<StateType, RootStateType>,
      payload: {
        smsCode: string;
        smsSceneType: SmsSceneType
      },
    ): Promise<SmsConfirmResult | boolean> {
      try {
        const { channel, submitResult, brokenProductId } = context.state;
        const { smsSceneType, smsCode } = payload || {};

        if (channel) {
          let confirmResult;
          if (smsSceneType && smsSceneType === SmsSceneType.BROKEN) {
            // 增验订单 正向流程 传 选中渠道的渠道 id。中断流程，传 中断订单 id
            // 中断时短信校验, 传中断订单对应的渠道 id
            confirmResult = await applyAfterSmsAuth(
              brokenProductId || channel.productId,
              smsCode,
              submitResult ? submitResult.loanOrderId : '',
            );
          } else {
            confirmResult = await confirmApplySms({
              smsCode,
              productId: channel.productId,
              loanOrderId: submitResult ? submitResult.loanOrderId : '',
              repaymentOrderMessageId: submitResult
                ? submitResult.repaymentOrderMessageId
                : '',
            });
          }

          return Promise.resolve(confirmResult);
        }

        return Promise.reject(new Error('请选择提现渠道'));
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // 获取防诈骗相关配置
    async getAntiFraudConfig(context: ActionContext<StateType, RootStateType>): Promise<void> {
      try {
        /**-
         * 配置地址：https://config.hellobike.cn/#/mobile/detail?env=fat&teamName=%E5%93%88%E5%95%B0%E5%87%BA%E8%A1%8C-%E9%87%91%E8%9E%8D-H5
         */
        const antiFraudConfig = await Vue.prototype.$tangram.getConfigValue({
          moduleCode: 'financial',
          keyCode: 'AppFinanceH5Web.PreventingFraud',
        });
        context.dispatch(UPDATE_STATE_TYPE, {
          antiFraudConfig,
        });
      } catch (err) {
        ubtSendError(err as Error);
      }
    },
    /** 根据所选产品状态来确认是否默认选择极速tab */
    initSpeedStatus(context: ActionContext<StateType, RootStateType>) {
      let showSpeed = false;
      const {
        rightCheckStatus, channel, is24NeedRightAndNormalIs36,
      } = context.state;
      const { isDoublePassageDefaultSelectNormal } = context.getters;
      // 自营
      if (channel && channel.productId === ProductCommodity.AUTARKY_B_COMMODITY) {
        // 双融担模式 默认选择"普通通道"的实验用户
        showSpeed = !isDoublePassageDefaultSelectNormal;
        // 只要有一次需要购买则标记为true
        context.dispatch(UPDATE_STATE_TYPE, {
          autrakyZhenxiangCardState: {
            ...context.state.autrakyZhenxiangCardState,
            autrakyNeedBuyZhenxiangCard: context.getters.needBuyZhenxiangCard,
          },
        });
      } else {
        // 联营
        if (rightCheckStatus) {
          showSpeed = !!rightCheckStatus.canPurchase;
        }
        // 24+单通道普通通道切换到轻资产场景，默认选择普通通道
        if (is24NeedRightAndNormalIs36) {
          showSpeed = false;
        }
      }

      store.dispatch('loanRight/updateShowSpeed', showSpeed);
    },
    /** 更新自营渠道提现金额: 总额度 && 临时额度 && 通知栏提示文案(临额) */
    async updateZyAmountByIssuedTempAmount(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        const {
          productCreditResultList,
          totalAvailableAmount,
        } = await getChannelList(undefined, context.state.recallPreCredit);
        const {
          channel, channelList, totalAvailableAmount: originTotalAvailableAmount,
        } = context.state;
        const reCreateChannelList = (target: ApplyChannelData) => channelList.map(
          (item) => (item.productId === target.productId ? target : item),
        );
        /** 原始自营渠道 */
        const originAutarkyChannel = channelList.find(
          (item) => item.productId === ProductCommodity.AUTARKY_B_COMMODITY,
        );

        const result = formatChannelList(productCreditResultList).some((target) => {
          /** 若提现总额度、渠道可用额度都未变化，则说明临额已更新，无需再次更新了 */
          if (
            target.productId === ProductCommodity.AUTARKY_B_COMMODITY
            && (originTotalAvailableAmount !== Number(totalAvailableAmount)
              || originAutarkyChannel?.availableAmount !== target.availableAmount)
          ) {
            /** 若当前展示的是自营渠道，则更新提现额度(只更新额度相关，其他资金路由不处理) */
            if (channel?.productId === ProductCommodity.AUTARKY_B_COMMODITY) {
              /** 更新自营渠道额度相关，避免额度滞后 */
              const updatedChannel = {
                ...channel,
                availableAmount: target.availableAmount,
                creditAmount: target.creditAmount,
                adjustQuota: target.adjustQuota,
              };
              /** 同步更新 product 中的额度信息，用于计算 maxAmount */
              const { product, productList } = context.state;
              const productKey = generateProductKey(channel.productId, channel.capitalCode);
              const updatedProduct = product?.userCredit ? {
                ...product,
                userCredit: {
                  ...product.userCredit,
                  availableAmount: target.availableAmount,
                },
              } : product;
              const updatedProductList = updatedProduct
                ? { ...productList, [productKey]: updatedProduct }
                : productList;
              context.dispatch(UPDATE_STATE_TYPE, {
                channel: updatedChannel,
                channelList: reCreateChannelList(updatedChannel),
                totalAvailableAmount: Number(totalAvailableAmount),
                product: updatedProduct,
                productList: updatedProductList,
              });
              /** 重新计算最大可提现金额（不覆盖用户已输入的金额） */
              context.dispatch('getMaxAmount', { notUpdateAmount: true });
              /** 临额更新后，重新获取优惠模块临额实验结果（页面初始化时可能没有临额，实验未执行） */
              context.dispatch('getDiscountModuleTempAmountExperimentRes');
            } else {
              /** 联营渠道静默更新自营（同步更新 productList 缓存，确保切换回自营时数据正确） */
              const { productList } = context.state;
              const productKey = generateProductKey(
                ProductCommodity.AUTARKY_B_COMMODITY,
                originAutarkyChannel?.capitalCode,
              );
              const cachedProduct = productList[productKey];
              const updatedProductList = cachedProduct?.userCredit
                ? {
                  ...productList,
                  [productKey]: {
                    ...cachedProduct,
                    userCredit: {
                      ...cachedProduct.userCredit,
                      availableAmount: target.availableAmount,
                    },
                  },
                }
                : productList;
              context.dispatch(UPDATE_STATE_TYPE, {
                channelList: reCreateChannelList(target),
                totalAvailableAmount: Number(totalAvailableAmount),
                productList: updatedProductList,
              });
            }

            return true;
          }

          return false;
        });
        /** 临额下发成功 & 更新后，埋点记录 */
        ubtSetupModuleExpose('finance_loan_temporaryLimit_detail', { result: result ? '1' : '0' });

        /** 若是主动提额场景，则展示弹窗 */
        if (result && context.state.tempAmountEntry.applying) {
          context.dispatch('handleTempAmountSuccess');
        }

        return Promise.resolve(result);
      } catch {
        return Promise.resolve(false);
      }
    },
    async getAutrakyProcessExpRes(context: ActionContext<StateType, RootStateType>) {
      // 非自营return,不做请求
      if (context.state.channel?.productId !== ProductCommodity.AUTARKY_B_COMMODITY
        || !platform.isEasyBike) {
        return Promise.resolve(true);
      }
      // 如果为初始状态，则调用接口
      if (context.state.autrakyProcessExpRes === AutrakyProcessExpEnum.DEFAULT
          && context.getters.isDoublePassagewayPattern) {
        // 请求结果
        try {
          // 如果接口在请求中，则等待请求完成，避免重复请求
          // 可让该方法getAutrakyProcessExpRes可以在多处地方调用，如强制依赖autrakyProcessExpRes值的场景
          if (isRequestingAutrakyProcessExpRes) {
            return Promise.resolve(true);
          }
          isRequestingAutrakyProcessExpRes = true;

          const { expGroupType } = await getGalileoExpResult({
            expScene: GalileoExpSceneEnum.CHANNEL_B_DOUBLE_CAPITAL_MODEL_AMOUNT_LIMIT_AB,
          });
          context.dispatch(UPDATE_STATE_TYPE, {
            autrakyProcessExpRes: expGroupType,
          });
          return Promise.resolve(true);
        } catch (e) {
          context.dispatch(UPDATE_STATE_TYPE, {
            autrakyProcessExpRes: AutrakyProcessExpEnum.CONTROL,
          });
          ubtSendError('getRightGalileoExpResult failed');
          return Promise.resolve(true);
        } finally {
          // 请求完成，重置状态
          isRequestingAutrakyProcessExpRes = false;
        }
      } else {
        return Promise.resolve(true);
      }
    },
    /** 右侧轻资产场景
     * 需求方案：https://hellobike.yuque.com/gpxxgb/oyyz8n/tnivcnbk9nrook6h
     * @return 是否能切换到普通通道
     * TODO: 做右侧可承接24轻资产需求时此方法名和打标is24NeedRightAndNormalIs36名称都需要换掉
     */
    async checkDualChannelLight36Mode(
      context: ActionContext<StateType, RootStateType>,
      payload?: {
        vueInstance?: {
          $root: {
            $emit: (event: string, data?: unknown) => void;
          };
        };
      },
    ): Promise<boolean> {
      try {
        // 获取当前选中渠道的rightLight36ProductId
        const {
          rightLight36ProductId, rightLightUncreditedProductId,
        } = context.state.channel || {};
        if (!rightLight36ProductId && !rightLightUncreditedProductId) {
          return true;
        }
        ubtSetupPageButton('finance_loan_right_hasLightProductId', {
          productId: rightLight36ProductId,
          rightLightUncreditedProductId,
        });

        // 调用检查切换资产模式接口，传入rightLight36ProductId
        const { checkResult, failReason } = await checkDualChannelLight36({
          productId: rightLight36ProductId,
          uncreditedProductId: rightLightUncreditedProductId,
        });

        if (failReason) {
          // 有失败原因，阻止切换
          Dialog.alert({
            title: '提示',
            message: failReason,
            confirmButtonText: '我知道了',
            closeable: false,
          });

          return false;
        }

        // 埋点上报接口调用结果
        ubtSetupPageButton('finance_loan_right_checkLightSuccess', {
          productId: rightLight36ProductId,
          result: checkResult,
        });

        // 如果结果是true，更新状态并发布事件
        if (checkResult) {
          // 更新is24NeedRightAndNormalIs36为true
          context.dispatch(UPDATE_STATE_TYPE, {
            is24NeedRightAndNormalIs36: true,
          });

          // 通过事件总线发布switchToNormalChannel消息，携带rightLight36ProductId参数
          if (payload?.vueInstance?.$root) {
            payload.vueInstance.$root.$emit('switchToNormalChannel', rightLight36ProductId);
          }
        }

        return true;
      } catch (error) {
        // 错误处理和上报，但不向外抛错，静默处理
        ubtSendError({
          name: 'finance_check_dual_channel_light36_error',
          message: error instanceof Error ? error.message : 'Unknown error',
        });

        // 静默处理，返回true，不影响用户其他操作
        return true;
      }
    },
    /** 判断切换回极速通道是否需要切回自营渠道
     * 需求方案：https://hellobike.yuque.com/gpxxgb/oyyz8n/tnivcnbk9nrook6h
     * Vuex 的所有 Action 都会被自动包装成 Promise，即使 action 函数本身是同步的
     */
    checkSwitchBackToSpeedChannel(
      context: ActionContext<StateType, RootStateType>,
      payload?: {
        vueInstance?: {
          $root: {
            $emit: (event: string, data?: unknown) => void;
          };
        };
      },
    ): boolean {
      try {
        // 获取当前渠道信息
        const { channel, is24NeedRightAndNormalIs36 } = context.state;
        // 埋点上报切换回自营渠道
        ubtSetupPageButton('finance_loan_right_lightBackAutarky', {
          productId: channel?.productId,
        });

        // 判断is24NeedRightAndNormalIs36是否为true

        if (is24NeedRightAndNormalIs36) {
          // 重置is24NeedRightAndNormalIs36为false
          context.dispatch(UPDATE_STATE_TYPE, {
            is24NeedRightAndNormalIs36: false,
          });

          // 通过事件总线发布switchToSpeedChannel消息
          if (payload?.vueInstance?.$root) {
            payload.vueInstance.$root.$emit('switchToSpeedChannel');
          }

          return true;
        }

        return false;
      } catch (error) {
        // 错误处理和上报，但不向外抛错，静默处理
        ubtSendError({
          name: 'finance_check_switch_back_to_speed_channel_error',
          message: error instanceof Error ? error.message : 'Unknown error',
        });

        // 静默处理，返回false，不影响用户其他操作
        return false;
      }
    },

    /**
     * 检查是否展示提额入口
     * @constraints
     *   1. 只有选中自营渠道（8180）时才请求接口
     *   2. 请求过一次后（showEntry !== undefined），切换渠道不再重复请求
     */
    async checkTempAmountEntryShow(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      const { channel, tempAmountEntry } = context.state;

      // 约束1：只有自营渠道（8180）才请求
      if (channel?.productId !== ProductCommodity.AUTARKY_B_COMMODITY) {
        return;
      }

      // 约束2：已请求过则不重复请求
      if (tempAmountEntry.showEntry !== undefined) {
        return;
      }

      try {
        const { showEntry } = await showLoanPageTempAmountEntry();
        context.dispatch(UPDATE_STATE_TYPE, {
          tempAmountEntry: { ...tempAmountEntry, showEntry },
        });

        if (showEntry) {
          ubtSetupModuleExpose('finance_loan_apply_tempAmountEntry');
        }
      } catch {
        context.dispatch(UPDATE_STATE_TYPE, {
          tempAmountEntry: { ...tempAmountEntry, showEntry: false },
        });
      }
    },

    /**
     * 触发临额申请（用户点击提额入口）
     * @description 复用 commonPollingNotifyIssued 的提现页轮询逻辑
     */
    async applyTempAmountEntry(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<void> {
      ubtSetupPageButton('finance_loan_apply_tempAmountEntry');

      // 调用临额申请接口
      const applyResult = await applyTempAmount(TempAmountSceneEnum.PREMIUM_CUSTOMER_WITHDRAWAL);

      const { tempAmountEntry } = context.state;
      context.dispatch(UPDATE_STATE_TYPE, {
        tempAmountEntry: { ...tempAmountEntry, clicked: true, applying: true },
      });

      // 申请成功后，更新状态并启动轮询
      if (applyResult.tempAmountApplyResult) {
        // 复用 commonPollingNotifyIssued 的轮询逻辑
        await store.dispatch('tempAmount/commonPollingNotifyIssued', {
          loopSceneList: [EventLoopNotifySceneEnum.TEMP_AMOUNT],
        });
        Toast('已提交提额申请，请耐心等待');
      } else {
        Toast('抱歉，提额审核未通过');
      }
    },

    /**
     * 处理提额成功弹窗展示
     * @description 在 updateZyAmountByIssuedTempAmount 成功后被调用
     */
    handleTempAmountSuccess(
      context: ActionContext<StateType, RootStateType>,
    ): void {
      const { tempAmountEntry } = context.state;
      if (!tempAmountEntry.applying) {
        return;
      }

      context.dispatch(UPDATE_STATE_TYPE, {
        tempAmountEntry: { ...tempAmountEntry, showSuccessPopup: true, applying: false },
      });
    },

    /**
     * 重置提额入口状态（离开页面时调用）
     */
    resetTempAmountEntryState(
      context: ActionContext<StateType, RootStateType>,
    ): void {
      context.dispatch(UPDATE_STATE_TYPE, {
        tempAmountEntry: {
          showEntry: undefined,
          clicked: false,
          applying: false,
          showSuccessPopup: false,
        },
      });
    },

  },
  modules: {
    marketingAmount: marketingAmountStore,
    discountModule: discountModuleStore,
    couponDiscountRate: couponDiscountRateStore,
    applyOptimize: applyOptimizeStore,
  },
};

store.registerModule(
  'applyProduct',
  generateMutations<StateType>(moduleStore),
);
